import { memo, type PropsWithChildren } from 'react'
import { InteractiveProvider } from './provider'
import { InteractiveTracker } from './tracker'
import { isInteractiveRenderer } from './window-props'
import { InteractiveDebugger } from './debugger'

export { refreshInteractiveBounds, syncInteractiveRegion } from './use-bounds-sync'
export { setOverlayInteractive, type InteractiveFrame } from './interactive-frame'

// Instruct the IDE + DOM that a custom `interactive-element` tag is supported
if (typeof window !== 'undefined' && typeof window?.customElements?.get === 'function') {
  // developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
  class IElement extends HTMLElement {}

  if (!window.customElements.get('interactive-element')) {
    window.customElements.define('interactive-element', IElement)
  }
}

type RootProps = PropsWithChildren<{
  className?: string
}>

/**
 * Pure bounds marker: registers its box (via the tracker) so the overlay grabs
 * the cursor over it. The interactive decision is a single cursor hit-test
 * against all registered bounds (see `use-hit-test`), not per-element pointer
 * events — so a `display: contents` wrapper with gaps between its children no
 * longer flickers to click-through.
 */
const Root = memo(({ children, className }: RootProps) => {
  return (
    <interactive-element className={className} style={{ display: 'contents' }}>
      {children}
      <InteractiveTracker />
    </interactive-element>
  )
})
Root.displayName = 'InteractiveElement'

const Placeholder = memo(({ children }: PropsWithChildren) => {
  return children
})
Placeholder.displayName = 'InteractivePlaceholder'

/**
 * When this component is rendered in the overlay, it will use the root interactive-element wrapper.
 * This enables the overlay window to become interactive or click-through when necessary. Otherwise,
 * in other renderers it will render the children without the interactive-element wrapper.
 */
export const Interactive = Object.assign(isInteractiveRenderer ? Root : Placeholder, {
  Provider: InteractiveProvider,
  Debugger: InteractiveDebugger,
})
